import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import type { UserProfileDetails } from 'src/replicant/friends/friends.asyncGetters';
import { Feature } from 'src/replicant/analytics';
import { AssetGroup } from '@play-co/timestep-core';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { UnfriendPlayerInfoPanel } from '../components/UnfriendPlayerInfoPanel';
import { PetEntity } from 'src/game/logic/EntityManager';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { petSpeciesAssets } from 'src/loadingGroups';
import { waitForAssets, withLoading } from 'src/lib/utils';

type CloseHandler = (success: boolean) => void;

export default class PopupUnfriendConfirmation extends PopupBasic {
  private content: FlexBoxView;
  private player: UnfriendPlayerInfoPanel;

  protected getAssetsGroup() {
    return new AssetGroup(UnfriendPlayerInfoPanel.assets);
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 800,
      darkerBg: false,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND._,
        $subFeature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND.CONFIRMATION_POPUP,
      },
    });

    // Remove default close button
    this.buttonClose.removeFromSuperview();

    // Create content wrapper container
    this.content = new FlexBoxView({
      superview: this.box,
      flex: {
        width: this.box.style.width,
        height: this.box.style.height,
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingBottom: 40,
      },
    });

    // Create player basic info panel
    this.player = new UnfriendPlayerInfoPanel(this.content);

    const buttonWidth = 230;
    const buttonHeight = 88;
    const gap = 16;

    // Create button row container
    const buttonRow = new FlexBoxView({
      superview: this.content,
      flex: {
        width: buttonWidth * 2 + gap,
        justifyContent: 'space-between',
        marginTop: 30,
      },
    });

    const baseButtonOpts: Partial<ButtonScaleViewWithTextOpts> = {
      superview: buttonRow,
      width: buttonWidth,
      height: buttonHeight,
      labelOffsetX: -5,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
    };

    // Create unfriend button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...baseButtonOpts,
      localeText: () => i18n('friends.unfriendConfirmation.unfriend'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND._,
        $subFeature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND.UNFRIEND,
      },
    });

    // Create keep friend button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...baseButtonOpts,
      ...uiConfig.buttons.secondary,
      disabledFontOffsetY: -1,
      localeText: () => i18n('friends.unfriendConfirmation.keepFriend'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND._,
        $subFeature: Feature.NEWUI.NAV_FRIENDS.UNFRIEND.KEEP_FRIEND,
      },
    });
  }

  public init({ details }: { details: UserProfileDetails }) {
    super.init({
      title: i18n('friends.unfriendConfirmation.title'),
      message: i18n('friends.unfriendConfirmation.message'),
    });

    this.message.updateOpts({ size: 27, offsetY: 45 });
    this.player.setData(details);

    // Create animated sad pet
    const species = details.currentEvolution.species;
    const pet = new PetEntity('unfriendPet', species);
    pet.updateOpts({
      superview: this.box,
      x: this.box.style.width * 0.5,
      y: 435,
      scale: 0.75,
      anchorX: 0.5 * pet.style.width,
      anchorY: 0.78 * pet.style.height,
    });
    const petAnimations = petAnimationNames[species];
    pet.setAnimation(petAnimations.sad.idle);

    const feature = Feature.PET._;
    const subFeature = Feature.PET.ASSETS;

    // Show local load spinner while pet species assets are being loaded.
    withLoading(() => petSpeciesAssets[species].load(), {
      superview: pet,
      scale: 1.25,
      feature,
      subFeature,
    });
  }
}
